import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Clock, ChevronDown, CheckCircle, ShieldCheck, MessageSquare, AlertCircle } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../utils/api';
import SEO from '../components/SEO';

const COUNTRY_CODES = [
  { code: '+91', flag: '🇮🇳', name: 'India' },
  { code: '+1',  flag: '🇺🇸', name: 'USA' },
  { code: '+44', flag: '🇬🇧', name: 'UK' },
  { code: '+971',flag: '🇦🇪', name: 'UAE' },
  { code: '+65', flag: '🇸🇬', name: 'Singapore' },
  { code: '+61', flag: '🇦🇺', name: 'Australia' },
  { code: '+49', flag: '🇩🇪', name: 'Germany' },
  { code: '+33', flag: '🇫🇷', name: 'France' },
  { code: '+86', flag: '🇨🇳', name: 'China' },
  { code: '+81', flag: '🇯🇵', name: 'Japan' },
];

// Step machine: FORM → EMAIL_OTP → SEND
const STEP = { FORM: 'form', EMAIL_OTP: 'email_otp', SEND: 'send' };

/* ── OTP input component ──────────────────────────────────────────────────── */
function OtpInput({ digits, onChange, onKeyDown, onPaste, refs, error }) {
  return (
    <div>
      <div className="flex justify-center gap-3" onPaste={onPaste}>
        {digits.map((d, i) => (
          <input key={i} ref={el => (refs.current[i] = el)}
            type="text" inputMode="numeric" maxLength={1} value={d}
            onChange={e => onChange(i, e.target.value)}
            onKeyDown={e => onKeyDown(i, e)}
            className={`w-12 h-14 text-center text-xl font-bold border-2 bg-white text-ink focus:outline-none transition-colors
              ${error ? 'border-red-400' : d ? 'border-gold' : 'border-dark-600 focus:border-gold'}`}
          />
        ))}
      </div>
      {error && <p className="text-red-500 text-xs mt-2 text-center">{error}</p>}
    </div>
  );
}

/* ── Verification badge ───────────────────────────────────────────────────── */
function VerifiedBadge({ label, value, onEdit }) {
  return (
    <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-lg">
      <CheckCircle size={16} className="text-emerald-500 shrink-0" />
      <div>
        <p className="text-emerald-700 text-xs font-semibold">{label} Verified</p>
        <p className="text-emerald-600 text-xs">{value}</p>
      </div>
      {onEdit && (
        <button type="button" onClick={onEdit} className="ml-auto text-xs text-gray-400 hover:text-gray-600">Change</button>
      )}
    </div>
  );
}

export default function Contact() {
  const [form, setForm]       = useState({ name: '', email: '', phone: '', message: '' });
  const [countryCode, setCC]  = useState('+91');
  const [ccOpen, setCCOpen]   = useState(false);
  const [errors, setErrors]   = useState({});
  const [loading, setLoading] = useState(false);
  const [siteContact, setSiteContact] = useState(null);
  const [duplicateBanner, setDuplicateBanner] = useState(null);

  // Step state
  const [step, setStep]               = useState(STEP.FORM);
  const [emailOtpToken, setEmailToken] = useState('');

  // Email OTP
  const [emailDigits, setEmailDigits] = useState(['','','','','','']);
  const [emailOtpErr, setEmailOtpErr] = useState('');
  const [emailTimer, setEmailTimer]   = useState(0);
  const emailRefs = useRef([]);

  const emailTimerRef = useRef(null);

  useEffect(() => {
    api.get('/settings').then(r => { if (r.data.settings?.contact) setSiteContact(r.data.settings.contact); }).catch(() => {});
  }, []);

  // Countdown timer for email OTP
  useEffect(() => {
    if (emailTimer <= 0) return;
    emailTimerRef.current = setTimeout(() => setEmailTimer(t => t - 1), 1000);
    return () => clearTimeout(emailTimerRef.current);
  }, [emailTimer]);

  const validate = () => {
    const e = {};
    if (!form.name.trim())  e.name    = 'Full name is required';
    if (!form.email.trim()) e.email   = 'Email address is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Enter a valid email address';
    if (!form.phone.trim()) e.phone   = 'Phone number is required';
    else if (form.phone.replace(/\D/g,'').length !== 10) e.phone = 'Phone number must be exactly 10 digits';
    if (!form.message.trim()) e.message = 'Message is required';
    return e;
  };

  // OTP digit helpers
  const makeOtpHandlers = (digits, setDigits, setErr, refs) => ({
    onChange: (i, val) => {
      const d = val.replace(/\D/g,'').slice(-1);
      const next = [...digits]; next[i] = d; setDigits(next); setErr('');
      if (d && i < 5) refs.current[i+1]?.focus();
    },
    onKeyDown: (i, e) => { if (e.key === 'Backspace' && !digits[i] && i > 0) refs.current[i-1]?.focus(); },
    onPaste: (e) => {
      e.preventDefault();
      const p = e.clipboardData.getData('text').replace(/\D/g,'').slice(0,6);
      if (p.length === 6) { setDigits(p.split('')); refs.current[5]?.focus(); }
    },
  });

  const emailHandlers = makeOtpHandlers(emailDigits, setEmailDigits, setEmailOtpErr, emailRefs);

  /* ─── Step 1: Send email OTP ─────────────────────────────────────────────── */
  const handleSendEmailOtp = async (e) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({}); setLoading(true);
    try {
      const res = await api.post('/contact/send-otp', { email: form.email });
      if (res.data.devMode && res.data.devOtp) {
        // Dev mode — auto-fill OTP boxes and show it prominently
        setEmailDigits(res.data.devOtp.split(''));
        toast.info(
          <div>
            <p className="font-semibold text-sm">Dev Mode — SMTP not configured</p>
            <p className="text-lg font-mono tracking-[0.3em] mt-1 font-bold">{res.data.devOtp}</p>
            <p className="text-xs mt-1 opacity-70">OTP auto-filled in boxes</p>
          </div>,
          { autoClose: 15000 }
        );
      } else {
        toast.info(`OTP sent to ${form.email}`);
      }
      setStep(STEP.EMAIL_OTP);
      setEmailDigits(res.data.devOtp ? res.data.devOtp.split('') : ['','','','','','']);
      setEmailOtpErr(''); setEmailTimer(30);
      setTimeout(() => emailRefs.current[0]?.focus(), 100);
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to send OTP'); }
    finally { setLoading(false); }
  };

  /* ─── Step 2: Verify email OTP — then go straight to SEND ──────────────── */
  const handleVerifyEmailOtp = async (e) => {
    e.preventDefault();
    const otp = emailDigits.join('');
    if (otp.length !== 6) { setEmailOtpErr('Please enter all 6 digits'); return; }
    setLoading(true); setEmailOtpErr('');
    try {
      const res = await api.post('/contact/verify-otp', { email: form.email, otp });
      setEmailToken(res.data.otpToken);
      setStep(STEP.SEND);
    } catch (err) { setEmailOtpErr('Email verification failed — ' + (err.response?.data?.message || 'Incorrect OTP. Please try again.')); }
    finally { setLoading(false); }
  };

  /* ─── Step 3: Submit inquiry ────────────────────────────────────────────── */
  const handleSubmit = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const res = await api.post('/contact/submit', {
        ...form,
        phone: `${countryCode}${form.phone}`,
        otpToken: emailOtpToken,
      });
      const num = res.data.inquiryNumber;
      toast.success(
        <div>
          <p className="font-semibold">Inquiry sent successfully!</p>
          <p className="text-xs mt-1">Reference: <span className="font-bold tracking-wider">{num}</span></p>
          <p className="text-xs mt-0.5">Confirmation sent to your email.</p>
        </div>,
        { autoClose: 10000 }
      );
      setForm({ name: '', email: '', phone: '', message: '' });
      setStep(STEP.FORM); setEmailToken('');
    } catch (err) {
      if (err.response?.status === 409 && err.response.data?.duplicate) {
        const { inquiryNumber } = err.response.data;
        setDuplicateBanner({ inquiryNumber });
        setStep(STEP.FORM);
        setEmailToken('');
        setEmailDigits(['','','','','','']);
      } else {
        const msg = err.response?.data?.message || 'Failed to send inquiry.';
        toast.error(msg);
        if (err.response?.status === 403) { setStep(STEP.FORM); setEmailToken(''); }
      }
    } finally { setLoading(false); }
  };

  const sel = COUNTRY_CODES.find(c => c.code === countryCode);
  const fullPhone = `${countryCode} ${form.phone}`;

  return (
    <>
      <SEO title="Contact Us" description="Get in touch with House of Sample." />

      <section className="relative h-[432px] flex items-end pb-16 overflow-hidden">
        <img src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80"
          alt="Contact" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 w-full">
          <h1 className="font-serif text-4xl md:text-5xl text-white">Contact Us</h1>
        </div>
      </section>

      <section className="py-24 px-6 max-w-7xl mx-auto grid md:grid-cols-2 gap-16">

        {/* Left — contact info */}
        <motion.div initial={{ opacity:0, x:-30 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }}>
          <p className="text-gold text-xs tracking-[0.3em] uppercase mb-4">Get In Touch</p>
          <h2 className="section-title text-left mb-6">We'd Love to Hear From You</h2>
          <div className="w-12 h-px bg-gold mb-8" />
          <div className="space-y-6 mb-10">
            {[
              { icon: MapPin, label: 'Address', value: siteContact?.address || '206, Multilevel Shed, Near Lions Club School, GIDC Naroda, Ahmedabad, Gujarat 382330' },
              { icon: Phone,  label: 'Phone',   value: siteContact?.phone   || '+91 85114 28123' },
              { icon: Mail,   label: 'Email',   value: siteContact?.email   || 'sales@houseofsample.in' },
              { icon: Clock,  label: 'Hours',   value: siteContact?.hours   || 'Mon–Sat: 9:00 AM – 6:00 PM' },
            ].map(({ icon: Icon, label, value }) => (
              <div key={label} className="flex items-start gap-4">
                <div className="w-10 h-10 border border-gold flex items-center justify-center shrink-0">
                  <Icon className="text-gold" size={16} />
                </div>
                <div>
                  <p className="text-xs tracking-widest uppercase text-gray-500 mb-1">{label}</p>
                  <p className="text-ink-600 text-sm">{value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="w-full h-64 bg-white border border-dark-600 overflow-hidden">
            <iframe title="Location"
              src={siteContact?.mapUrl?.includes('google.com/maps/embed') ? siteContact.mapUrl
                : "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3671.9!2d72.6697!3d23.0827!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDA0JzU3LjciTiA3MsKwNDAnMTAuOSJF!5e0!3m2!1sen!2sin!4v1703000000000"}
              width="100%" height="100%" style={{ border:0 }} allowFullScreen loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </motion.div>

        {/* Right — step-based form */}
        <motion.div initial={{ opacity:0, x:30 }} whileInView={{ opacity:1, x:0 }} viewport={{ once:true }}>

          {/* Progress indicator — 3 steps */}
          <div className="flex items-center gap-2 mb-8">
            {[
              { label: 'Details',   active: step === STEP.FORM },
              { label: 'Email OTP', active: step === STEP.EMAIL_OTP },
              { label: 'Send',      active: step === STEP.SEND },
            ].map((s, i, arr) => (
              <React.Fragment key={s.label}>
                <div className={`flex items-center gap-1.5 text-xs font-medium px-2 py-1 rounded-full transition-colors
                  ${s.active ? 'bg-gold text-white' : (
                    (step === STEP.EMAIL_OTP && i < 1) ||
                    (step === STEP.SEND && i < 3)
                  ) ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-400'}`}>
                  {((step === STEP.EMAIL_OTP && i < 1) || (step === STEP.SEND && i < 3)) && '✓ '}
                  {s.label}
                </div>
                {i < arr.length - 1 && <div className="flex-1 h-px bg-gray-200" />}
              </React.Fragment>
            ))}
          </div>

          {/* ── STEP: EMAIL OTP ─────────────────────────────────────────── */}
          {step === STEP.EMAIL_OTP && (
            <div className="space-y-6">
              <div className="text-center pb-4 border-b border-dark-600">
                <div className="w-14 h-14 rounded-full bg-gold/10 border-2 border-gold flex items-center justify-center mx-auto mb-4">
                  <Mail className="text-gold" size={24} />
                </div>
                <h3 className="font-serif text-2xl text-ink mb-2">Verify Your Email</h3>
                <p className="text-ink-400 text-sm">OTP sent to <span className="font-semibold text-ink">{form.email}</span></p>
              </div>
              <form onSubmit={handleVerifyEmailOtp} className="space-y-6">
                <div>
                  <label className="block text-xs tracking-widest uppercase text-ink-400 mb-4 text-center">Enter 6-digit OTP</label>
                  <OtpInput digits={emailDigits} onChange={emailHandlers.onChange}
                    onKeyDown={emailHandlers.onKeyDown} onPaste={emailHandlers.onPaste}
                    refs={emailRefs} error={emailOtpErr} />
                  <p className="text-ink-400 text-xs text-center mt-2">Expires in 10 minutes</p>
                </div>
                <button type="submit" disabled={loading || emailDigits.join('').length !== 6}
                  className="btn-gold w-full disabled:opacity-50">
                  {loading ? 'Verifying...' : 'Verify Email OTP'}
                </button>
                <div className="flex items-center justify-between text-sm">
                  <button type="button" onClick={async () => {
                    if (emailTimer > 0 || loading) return;
                    setLoading(true);
                    try {
                      const res = await api.post('/contact/send-otp', { email: form.email });
                      if (res.data.devMode && res.data.devOtp) {
                        setEmailDigits(res.data.devOtp.split(''));
                        toast.info(
                          <div>
                            <p className="font-semibold text-sm">Dev Mode — New OTP</p>
                            <p className="text-lg font-mono tracking-[0.3em] mt-1 font-bold">{res.data.devOtp}</p>
                            <p className="text-xs mt-1 opacity-70">Auto-filled in boxes</p>
                          </div>,
                          { autoClose: 15000 }
                        );
                      } else {
                        toast.info('New OTP sent');
                      }
                      setEmailOtpErr('');
                      setEmailTimer(30);
                    } catch (err) { toast.error(err.response?.data?.message || 'Failed to resend OTP'); }
                    finally { setLoading(false); }
                  }} disabled={emailTimer > 0 || loading}
                    className={emailTimer > 0 ? 'text-ink-400 cursor-not-allowed' : 'text-gold hover:underline'}>
                    {emailTimer > 0 ? `Resend in ${emailTimer}s` : 'Resend OTP'}
                  </button>
                  <button type="button" onClick={() => { setStep(STEP.FORM); setEmailOtpErr(''); }}
                    className="text-ink-400 hover:text-gold transition-colors">← Back</button>
                </div>
              </form>
            </div>
          )}

          {/* ── STEP: FORM + SEND ───────────────────────────────────────── */}
          {(step === STEP.FORM || step === STEP.SEND) && (
            <form onSubmit={step === STEP.SEND ? handleSubmit : handleSendEmailOtp} className="space-y-5" noValidate>

              {/* Email verified badge when in SEND step */}
              {step === STEP.SEND && (
                <div className="space-y-2">
                  <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-lg">
                    <CheckCircle size={16} className="text-emerald-500 shrink-0" />
                    <div>
                      <p className="text-emerald-700 text-xs font-semibold">✓ Email Verified</p>
                      <p className="text-emerald-600 text-xs">{form.email}</p>
                    </div>
                    <button type="button" onClick={() => { setStep(STEP.FORM); setEmailToken(''); }}
                      className="ml-auto text-xs text-gray-400 hover:text-gray-600">Change</button>
                  </div>
                </div>
              )}

              {duplicateBanner && (
                <div className="mb-6 bg-amber-50 border border-amber-300 rounded-lg px-5 py-4 flex items-start gap-3">
                  <AlertCircle size={18} className="text-amber-500 mt-0.5 shrink-0" />
                  <div className="flex-1">
                    <p className="text-amber-800 font-semibold text-sm">Inquiry Already Open</p>
                    <p className="text-amber-700 text-sm mt-1">
                      Your inquiry <span className="font-bold font-mono tracking-wider">{duplicateBanner.inquiryNumber}</span> is already open.
                      We've sent a reminder to your email and phone.
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setDuplicateBanner(null)}
                    className="text-amber-500 hover:text-amber-700 text-xs underline shrink-0 mt-0.5"
                  >
                    Dismiss
                  </button>
                </div>
              )}

              {/* Full Name */}
              <div>
                <label className="block text-xs tracking-widest uppercase text-ink-400 mb-2">Full Name <span className="text-red-500">*</span></label>
                <input type="text" value={form.name} disabled={step === STEP.SEND}
                  onChange={e => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors(v => ({ ...v, name: '' })); }}
                  className={`w-full bg-white border text-ink px-4 py-3 text-sm focus:outline-none transition-colors
                    ${step === STEP.SEND ? 'opacity-60 cursor-not-allowed' : ''}
                    ${errors.name ? 'border-red-400' : 'border-dark-600 focus:border-gold'}`} />
                {errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
              </div>

              {/* Email */}
              <div>
                <label className="block text-xs tracking-widest uppercase text-ink-400 mb-2">
                  Email Address <span className="text-red-500">*</span>
                  {step === STEP.SEND && <span className="ml-2 normal-case text-emerald-500">✓ Verified</span>}
                </label>
                <input type="email" value={form.email} disabled={step === STEP.SEND}
                  onChange={e => { setForm({ ...form, email: e.target.value }); if (errors.email) setErrors(v => ({ ...v, email: '' })); }}
                  className={`w-full bg-white border text-ink px-4 py-3 text-sm focus:outline-none transition-colors
                    ${step === STEP.SEND ? 'opacity-60 cursor-not-allowed bg-emerald-50 border-emerald-200' : ''}
                    ${errors.email ? 'border-red-400' : 'border-dark-600 focus:border-gold'}`} />
                {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
              </div>

              {/* Phone */}
              <div>
                <label className="block text-xs tracking-widest uppercase text-ink-400 mb-2">
                  Phone Number <span className="text-red-500">*</span>
                  <span className="ml-2 normal-case text-gray-400 font-normal">(10 digits)</span>
                </label>
                <div className="flex">
                  <div className="relative shrink-0">
                    <button type="button" onClick={() => step === STEP.FORM && setCCOpen(o => !o)}
                      disabled={step === STEP.SEND}
                      className={`flex items-center gap-1.5 h-full px-3 py-3 bg-white border text-sm focus:outline-none border-r-0
                        ${step === STEP.SEND ? 'opacity-60 cursor-not-allowed' : ''}
                        ${errors.phone ? 'border-red-400' : 'border-dark-600'}`}>
                      <span>{sel?.flag}</span>
                      <span className="text-ink font-medium">{countryCode}</span>
                      <ChevronDown size={12} className={`text-gray-400 transition-transform ${ccOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {ccOpen && step === STEP.FORM && (
                      <div className="absolute top-full left-0 z-50 bg-white border border-dark-600 shadow-xl w-52 max-h-56 overflow-y-auto">
                        {COUNTRY_CODES.map(c => (
                          <button key={c.code} type="button"
                            onClick={() => { setCC(c.code); setCCOpen(false); }}
                            className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm hover:bg-gray-50 text-left
                              ${countryCode === c.code ? 'bg-gold/10 text-gold' : 'text-ink-600'}`}>
                            <span>{c.flag}</span><span className="flex-1">{c.name}</span>
                            <span className="text-gray-400 text-xs">{c.code}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <input type="tel" value={form.phone} maxLength={10} disabled={step === STEP.SEND}
                    onChange={e => { const v = e.target.value.replace(/\D/g,'').slice(0,10); setForm({...form,phone:v}); if(errors.phone)setErrors(x=>({...x,phone:''})); }}
                    placeholder="98765 43210"
                    className={`flex-1 bg-white border text-ink px-4 py-3 text-sm focus:outline-none transition-colors
                      ${step === STEP.SEND ? 'opacity-60 cursor-not-allowed' : ''}
                      ${errors.phone ? 'border-red-400' : 'border-dark-600 focus:border-gold'}`} />
                </div>
                {errors.phone && <p className="text-red-500 text-xs mt-1">{errors.phone}</p>}
              </div>

              {/* Message */}
              <div>
                <label className="block text-xs tracking-widest uppercase text-ink-400 mb-2">Message <span className="text-red-500">*</span></label>
                <textarea rows={4} value={form.message}
                  onChange={e => { setForm({...form,message:e.target.value}); if(errors.message)setErrors(v=>({...v,message:''})); }}
                  className={`w-full bg-white border text-ink px-4 py-3 text-sm focus:outline-none transition-colors resize-none
                    ${errors.message ? 'border-red-400' : 'border-dark-600 focus:border-gold'}`} />
                {errors.message && <p className="text-red-500 text-xs mt-1">{errors.message}</p>}
              </div>

              {/* Submit button */}
              {step === STEP.FORM ? (
                <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-50 flex items-center justify-center gap-2">
                  <ShieldCheck size={16} /> {loading ? 'Sending OTP...' : 'Verify Email & Continue'}
                </button>
              ) : (
                <button type="submit" disabled={loading} className="btn-gold w-full disabled:opacity-50 flex items-center justify-center gap-2">
                  <MessageSquare size={16} /> {loading ? 'Sending...' : 'Send Inquiry'}
                </button>
              )}

              {step === STEP.FORM && (
                <p className="text-center text-xs text-ink-400">
                  We'll verify your email with an OTP before sending.
                </p>
              )}
            </form>
          )}

        </motion.div>
      </section>
    </>
  );
}
